<template>
  <div class="floating-pane fading overall-summary">
    <div class="floating-pane-header">
      <icon-summary />
      <span>{{ capitalize($t('names.summary', { n: 1 })) }}</span>
    </div>
    <div class="floating-pane-content">
      <div class="flex-form">
        <template v-if="showCampaignDetails">
          <div>
            <div>
              <icon-colored-campaign />
              {{ capitalize($t('names.campaign', { n: 1 })) }}:
            </div>
            <div>{{ campaignName }}</div>
          </div>
          <div>
            <div>
              <icon-colored-advertiser />
              {{ capitalize($t('names.advertiser', { n: 1 })) }}:
            </div>
            <div>
              <img
                v-if="advertiserLogo != null"
                :src="advertiserLogo"
                alt="" />
              <span v-else>{{ advertiserName }}</span>
            </div>
          </div>
          <div v-if="accountName != null">
            <div>
              <icon-colored-account />
              {{ capitalize($t('names.account', { n: 1 })) }}:
            </div>
            <div>
              <img
                v-if="accountLogo != null"
                :src="accountLogo"
                alt="" />
              <span v-else>{{ accountName }}</span>
            </div>
          </div>
        </template>
        <template v-if="startDate != null && endDate != null">
          <div>
            <div>
              <icon-colored-calendar />
              {{ capitalize($t('names.date', { n: 2 })) }}
            </div>
            <div>
              {{ dateRangeFormatted }}
            </div>
          </div>
        </template>
        <div v-if="hasMediaTypesInCart">
          <div>
            <icon-colored-media-type />
            {{ capitalize($t('names.venueType', { n: 2 })) }}
          </div>
          <div v-tooltip.left="mediaTypesInCartDescriptionHTML">
            <span>{{ includedMediaTypesInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedMediaTypes.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedMediaTypesInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasMarketsInCart">
          <div>
            <icon-colored-market />
            {{ capitalize($t('names.market', { n: 2 })) }}
          </div>
          <div v-tooltip="marketsInCartDescription">{{ marketsInCartDescription }}</div>
        </div>
        <div v-if="hasNetworksInCart">
          <div>
            <icon-colored-network />
            {{ capitalize($t('names.network', { n: 2 })) }}
          </div>
          <div v-tooltip="networksInCartDescription">{{ networksInCartDescription }}</div>
        </div>
        <div v-if="hasSitesInCart">
          <div>
            <icon-colored-site />
            {{ capitalize($t('names.site', { n: 2 })) }}
          </div>
          <div v-tooltip.left="sitesInCartDescription">
            <span>{{ includedSitesInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedSites.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedSitesInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasUnitsInCart">
          <div>
            <icon-colored-screen />
            {{ capitalize($t('names.screen', { n: 2 })) }}
          </div>
          <div v-tooltip.left="unitsInCartDescription">
            <span>{{ includedUnitsInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedUnits.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedUnitsInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasTagsInCart">
          <div>
            <icon-colored-media-type />
            {{ capitalize($t('names.tag', { n: 2 })) }}
          </div>
          <div v-tooltip="tagsInCartDescription">
            <span>{{ includedTagsInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedTags.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedTagsInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasRegionsInCart">
          <div>
            <icon-colored-location />
            {{ capitalize($t('names.region', { n: 2 })) }}
          </div>
          <div v-tooltip.left="regionsInCartDescription">
            <span>{{ includedRegionsInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedRegions.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedRegionsInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasCitiesInCart">
          <div>
            <icon-colored-city />
            {{ capitalize($t('names.city', { n: 2 })) }}
          </div>
          <div v-tooltip.left="citiesInCartDescriptionHTML">
            <span>{{ includedCitiesInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedCities.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedCitiesInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasGeofenceGroupsInCart">
          <div>
            <icon-colored-geofence-group />
            {{ capitalize($t('names.proximity', { n: 1 })) }}
          </div>
          <div v-tooltip.left="geofenceGroupsInCartDescription">
            {{ geofenceGroupsInCartDescription }}
            <span>{{ includedGeofenceGroupsInCartDescription }}</span>
            <span v-if="overallMap?.cart?.includedGeofenceGroups.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedGeofenceGroupsInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasLanguagesInCart">
          <div>
            <icon-colored-language />
            {{ capitalize($t('names.language', { n: 2 })) }}
          </div>
          <div v-tooltip.left="languagesInCartDescription">
            <span>{{ includedLanguagesInCartDescription }}</span>
            <span v-if="includedLanguages?.length > 0 && excludedLanguages?.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedLanguagesInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasCreativeOrientationsInCart">
          <div>
            <icon-colored-orientation-landscape />
            {{ capitalize($t('names.creativeOrientation', { n: 2 })) }}
          </div>
          <div v-tooltip.left="creativeOrientationsInCartDescription">
            <span>{{ includedCreativeOrientationsInCartDescription }}</span>
            <span v-if="includedCreativeOrientations?.length > 0 && excludedCreativeOrientations?.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedCreativeOrientationsInCartDescription }}</span>
          </div>
        </div>
        <div v-if="hasIabCategoriesInCart">
          <div>
            <icon-colored-iab />
            {{ capitalize($t('names.category', { n: 2 })) }}
          </div>
          <div v-tooltip.left="iabCategoriesInCartDescription">
            <span>{{ includedIabCategoriesInCartDescription }}</span>
            <span v-if="includedIabCategories?.length > 0 && excludedIabCategories?.length > 0">, </span>
            <span style="text-decoration: line-through">{{ excludedIabCategoriesInCartDescription }}</span>
          </div>
        </div>
        <div v-if="audienceDescription != null">
          <div>
            <icon-colored-demographic />
            {{ capitalize($t('names.audience', { n: 1 })) }}
          </div>
          <div>{{ audienceDescription }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, Ref } from 'vue';

  import {
    IconColoredAccount,
    IconColoredAdvertiser,
    IconColoredCalendar,
    IconColoredCampaign,
    IconColoredCity,
    IconColoredDemographic,
    IconColoredGeofenceGroup,
    IconColoredIab,
    IconColoredLanguage,
    IconColoredLocation,
    IconColoredMarket,
    IconColoredMediaType,
    IconColoredNetwork,
    IconColoredOrientationLandscape,
    IconColoredScreen,
    IconColoredSite,
  } from '@/common/icons/colored';
  import { IconSummary } from '@/common/icons/monochrome';
  import { isBoolean } from '@/globals';
  import OverallMap from '@/org-app/components/inventory/overallMap/OverallMap.vue';
  import i18n from '@/vueI18n';

  interface IOverallSummaryProps {
    areCampaignDetailsFilledIn: boolean;
    advertiserName?: string;
    advertiserLogo?: string;
    accountName?: string;
    accountLogo?: string;
    requiresCampaignCreation: boolean | null;
    audienceDescription?: string;
    startDate: DateTime;
    endDate: DateTime;
    campaignName?: string;
    overallMap?: InstanceType<typeof OverallMap>;
    alwaysOn?: boolean;
  }

  const props = defineProps<IOverallSummaryProps>();

  const cartLength = computed(() => props.overallMap?.cart?.cart?.length);

  const showCampaignDetails = computed(
    () =>
      props.areCampaignDetailsFilledIn ||
      (isBoolean(props.requiresCampaignCreation) && !props.requiresCampaignCreation),
  );

  function stringifyCartItems(cartItems: { name: string }[]): string {
    if (cartItems == null || cartLength.value === 0) {
      return null;
    }
    return cartItems.map((x) => x.name).join(', ');
  }

  function descriptionHtmlCartItems(
    includeProperty: string,
    excludeProperty: string,
    includeDescription: Ref<string>,
    excludeDescription: Ref<string>,
  ): string {
    if (props.overallMap?.cart == null) return null;

    let html = '';
    if (props.overallMap?.cart[includeProperty]?.length > 0) {
      html = `${html}<span>${includeDescription.value}</span>`;
    }
    if (props.overallMap?.cart[includeProperty]?.length > 0 && props.overallMap?.cart[excludeProperty]?.length > 0) {
      html = `${html}, `;
    }
    html = `${html}<span style="text-decoration: line-through;">${html !== '' ? ', ' : ''}${excludeDescription.value}</span>`;
    return html;
  }

  //
  // Media Types
  //
  const hasMediaTypesInCart = computed(
    () => props.overallMap?.isLoaded && props.overallMap?.cart?.mediaTypesInCart?.length > 0,
  );
  const includedMediaTypesInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.includedMediaTypes),
  );
  const excludedMediaTypesInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.excludedMediaTypes),
  );
  const mediaTypesInCartDescriptionHTML = computed(() =>
    descriptionHtmlCartItems(
      'includedMediaTypes',
      'excludedMediaTypes',
      includedMediaTypesInCartDescription,
      excludedMediaTypesInCartDescription,
    ),
  );

  //
  // Markets
  //
  const hasMarketsInCart = computed(
    () => props.overallMap?.isLoaded && props.overallMap?.cart?.marketsInCart?.length > 0,
  );
  const marketsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.marketsInCart));

  //
  // Networks
  //
  const hasNetworksInCart = computed(
    () => props.overallMap?.isLoaded && props.overallMap?.cart?.networksInCart?.length > 0,
  );
  const networksInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.networksInCart));

  //
  // Sites
  //
  const hasSitesInCart = computed(() => props.overallMap?.isLoaded && props.overallMap?.cart?.sitesInCart?.length > 0);
  const sitesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.sitesInCart));
  const includedSitesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.includedSites));
  const excludedSitesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.excludedSites));

  //
  // Units
  //
  const hasUnitsInCart = computed(() => props.overallMap?.isLoaded && props.overallMap?.cart?.unitsInCart?.length > 0);
  const unitsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.unitsInCart));
  const includedUnitsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.includedUnits));
  const excludedUnitsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.excludedUnits));

  //
  // Tags
  //
  const hasTagsInCart = computed(() => props.overallMap?.isLoaded && props.overallMap?.cart?.tagsInCart?.length > 0);
  const tagsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.tagsInCart));
  const includedTagsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.includedTags));
  const excludedTagsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.excludedTags));

  //
  // Regions
  //
  const hasRegionsInCart = computed(
    () => props.overallMap?.isLoaded && props.overallMap?.cart?.regionsInCart?.length > 0,
  );
  const regionsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.regionsInCart));
  const includedRegionsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.includedRegions));
  const excludedRegionsInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.excludedRegions));

  //
  // Cities
  //
  const hasCitiesInCart = computed(() => props.overallMap?.cart?.citiesInCart?.length > 0);
  const includedCitiesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.includedCities));
  const excludedCitiesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.excludedCities));
  const citiesInCartDescriptionHTML = computed(() =>
    descriptionHtmlCartItems(
      'includedCities',
      'excludedCities',
      includedCitiesInCartDescription,
      excludedCitiesInCartDescription,
    ),
  );

  //
  // Geofences
  //
  const hasGeofenceGroupsInCart = computed(() => props.overallMap?.cart?.geofenceGroupsInCart?.length > 0);
  const geofenceGroupsInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.geofenceGroupsInCart),
  );
  const includedGeofenceGroupsInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.includedGeofences),
  );
  const excludedGeofenceGroupsInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.excludedGeofences),
  );

  // Languages
  const hasLanguagesInCart = computed(() => props.overallMap?.cart?.languagesInCart?.length > 0);
  const includedLanguages = computed(() => props.overallMap?.cart?.includedLanguages);
  const excludedLanguages = computed(() => props.overallMap?.cart?.excludedLanguages);
  const languagesInCartDescription = computed(() => stringifyCartItems(props.overallMap?.cart?.languagesInCart));
  const includedLanguagesInCartDescription = computed(() => stringifyCartItems(includedLanguages.value));
  const excludedLanguagesInCartDescription = computed(() => stringifyCartItems(excludedLanguages.value));

  //
  // Creative Orientations
  //
  const hasCreativeOrientationsInCart = computed(() => props.overallMap?.cart?.creativeOrientationsInCart?.length > 0);
  const includedCreativeOrientations = computed(() => props.overallMap?.cart?.includedCreativeOrientations);
  const excludedCreativeOrientations = computed(() => props.overallMap?.cart?.excludedCreativeOrientations);
  const creativeOrientationsInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.creativeOrientationsInCart),
  );
  const includedCreativeOrientationsInCartDescription = computed(() =>
    stringifyCartItems(includedCreativeOrientations.value),
  );
  const excludedCreativeOrientationsInCartDescription = computed(() =>
    stringifyCartItems(excludedCreativeOrientations.value),
  );

  //
  // Iab Categories
  //
  const hasIabCategoriesInCart = computed(() => props.overallMap?.cart?.iabCategoriesInCart?.length > 0);
  const includedIabCategories = computed(() => props.overallMap?.cart?.includedIabCategories);
  const excludedIabCategories = computed(() => props.overallMap?.cart?.excludedIabCategories);
  const iabCategoriesInCartDescription = computed(() =>
    stringifyCartItems(props.overallMap?.cart?.iabCategoriesInCart),
  );
  const includedIabCategoriesInCartDescription = computed(() => stringifyCartItems(includedIabCategories.value));
  const excludedIabCategoriesInCartDescription = computed(() => stringifyCartItems(excludedIabCategories.value));

  //
  // Date Range
  //
  const dateRangeFormatted = computed(() =>
    props.alwaysOn
      ? `${props.startDate.toLocaleString(DateTime.DATE_SHORT)} – ${capitalize(i18n.global.t('messages.alwaysOn'))}`
      : `${props.startDate.toLocaleString(DateTime.DATE_SHORT)} – ${props.endDate.toLocaleString(DateTime.DATE_SHORT)}`,
  );
</script>
